// Subpath entry: rollup combine vocabulary shared by the graph service and the client.
// Pure functions and constants only — no service or React imports.

import type { RollupWeighting, StatusValue } from 'types/spherePoints';

export const ROLLUP_WEIGHTINGS: readonly RollupWeighting[] = ['equal', 'influence'];

/** Statuses a factor may report when coverage falls below its floor. `on_track` is excluded: a thin population must never read as healthy. */
export const BELOW_COVERAGE_STATUSES: readonly StatusValue[] = [
  'at_risk',
  'needs_attention',
  'critical',
  'blocked',
  'unknown',
];

export function isRollupWeighting(value: unknown): value is RollupWeighting {
  return typeof value === 'string' && (ROLLUP_WEIGHTINGS as readonly string[]).includes(value);
}

/** Raw stored values: a `RollupWeighting` / `AggregationMethod`, or a legacy or unknown string. */
interface ResolveRollupWeightingInput {
  weighting?: string | null;
  aggregationMethod?: string | null;
  influenceFloor?: number | null;
}

/**
 * The weighting a factor computes with. A stored `weighting` wins. A legacy factor without one
 * resolves to whatever reproduces its pre-`weighting` result: `weighted`, `majority` and
 * `percentage` always read influence, `worst` reads it only through a floor, and `average`
 * (or no method) never did.
 */
export function resolveRollupWeighting(input: ResolveRollupWeightingInput): RollupWeighting {
  if (isRollupWeighting(input.weighting)) {
    return input.weighting;
  }
  switch (input.aggregationMethod) {
    case 'weighted':
    case 'majority':
    case 'percentage':
      return 'influence';
    case 'worst':
      return typeof input.influenceFloor === 'number' ? 'influence' : 'equal';
    default:
      return 'equal';
  }
}

const LEGACY_EMPTY_COLOUR_TO_STATUS: Readonly<Record<string, StatusValue>> = {
  green: 'on_track',
  yellow: 'at_risk',
  red: 'critical',
};

const ROLLUP_STATUS_VALUES: readonly StatusValue[] = [
  'on_track',
  'at_risk',
  'needs_attention',
  'critical',
  'blocked',
  'unknown',
];

/** True for a value a caller may store as an empty-population status: a `StatusValue` or a legacy traffic-light colour. */
export function isAllowedEmptyBehavior(value: unknown): boolean {
  return (
    typeof value === 'string' &&
    (Object.hasOwn(LEGACY_EMPTY_COLOUR_TO_STATUS, value) || (ROLLUP_STATUS_VALUES as readonly string[]).includes(value))
  );
}

/** Normalise a stored empty-behaviour value (legacy colour, status or junk) to a `StatusValue`; anything unrecognised is `unknown`. */
export function normaliseEmptyBehavior(raw: unknown): StatusValue {
  if (typeof raw !== 'string') {
    return 'unknown';
  }
  if (Object.hasOwn(LEGACY_EMPTY_COLOUR_TO_STATUS, raw)) {
    return LEGACY_EMPTY_COLOUR_TO_STATUS[raw];
  }
  return (ROLLUP_STATUS_VALUES as readonly string[]).includes(raw) ? (raw as StatusValue) : 'unknown';
}
